import { SlideRenderer } from '@src/slide/application/service/similarity-collection/renderer/slide-renderer';
import {
    ISimilarityCollectionService, ISimilarityCollectionServiceDelegates,
} from '@src/slide/application/service/similarity-collection/similarity-collection-service.interface';
import { canvasToPt, pxToRealPt } from '@src/slide/infrastructure/module/independent/unit';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { SlideDataModel } from '@tencent/slide-sdk-model';
import { IStatePointEnum } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

declare global {
    interface Window {
        similarityCollectionService: Promise<ISimilarityCollectionService>;
    }
}

const DEFAULT_SCALE = 1;

export default class SimilarityCollection implements ISimilarityCollectionService {
    private services: ISimilarityCollectionServiceDelegates;
    private serviceReadyResolve;
    private servicesReady = new Promise(resolve => this.serviceReadyResolve = resolve);

    public setServiceDelegates = (services: ISimilarityCollectionServiceDelegates) => {
        this.services = {
            ...this.services,
            ...services,
        };
        this.serviceReadyResolve();
    };

    public async ensurePageReady() {
        await this.servicesReady;
        await this.services.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.visibleSlideRender].getPointCompletePromise();
    }

    public async collectData(params) {
        const { slideWidth, slideHeight } = this.pageSetUp;
        const slideData: any[] = [];
        const slideRendererCollection = await this.getSlideRendererCollection();
        for (const slideRenderer of slideRendererCollection) {
            slideData.push(await slideRenderer.collectData());
        }
        const slideImages: any[] = [];
        for (const slideRenderer of slideRendererCollection) {
            slideImages.push(await slideRenderer.collectImage(params));
        }
        return {
            presentation: {
                slideWidth: pxToRealPt(slideWidth),
                slideHeight: pxToRealPt(slideHeight),
                count: slideData.length,
            },
            slides: slideData,
            images: slideImages,
        };
    }

    public get pageSetUp() {
        const [slide] = this.services.uiDataService.presentation.children;
        const { slideWidth, slideHeight } = this.services.uiDataService.presentation.props;
        const { scale } = this.services.editAreaCanvasSizeManager.getCanvasSize();
        const defaultSetUp = {
            slideWidth: Math.round(slideWidth * scale),
            slideHeight: Math.round(slideHeight * scale),
        };
        if (!slide) {
            return defaultSetUp;
        }

        const slideEditor = this.services.pcEditorAreaService.forceBuildSlideSync(slide);
        const renderComponent = slideEditor.getBackgroundAppService()?.getComponent();

        if (!renderComponent) {
            return defaultSetUp;
        }

        return {
            slideWidth: Math.round(renderComponent.worldBounds.width),
            slideHeight: Math.round(renderComponent.worldBounds.height),
        };
    }

    public get slides() {
        return this.services.uiDataService.presentation.children;
    }

    public async getSlideRendererCollection(): Promise<SlideRenderer[]> {
        if (!this.services.slideRenderStateManager.getStatePoints()[IStatePointEnum.allSlideConvert].isCompleted) {
            await this.allSlideDataConvert();
        }
        const slideRendererArr: SlideRenderer[] = [];
        for (const slide of this.slides) {
            // 合并最新代码后 尝试用waitForDocumentReady替换
            await new Promise((resolve) => {
                (slide.sdkData as unknown as SlideDataModel).waitForLoading(() => resolve(true));
            });
            if (!slide.childrenConverted) {
                await slide.startConvertChildrenTask(Infinity);
            }
            if (!slide.getRelationMasterModel().childrenConverted) {
                await slide.getRelationMasterModel().startConvertChildrenTask(Infinity);
            }
            if (!slide.getRelationLayoutModel().childrenConverted) {
                await slide.getRelationLayoutModel().startConvertChildrenTask(Infinity);
            }
            const slideEditor = this.services.pcEditorAreaService.forceBuildSlideSync(slide, { isCopyToImage: true });
            const slideRenderer = new SlideRenderer(this.services, slideEditor);
            slideRendererArr.push(slideRenderer);
        }

        return slideRendererArr;
    }

    public async setPageScale(scale = DEFAULT_SCALE) {
        this.services.editAreaCanvasSizeManager.setCanvasScaleAndIsAutoAdapter(canvasToPt(scale), false);
        window.dispatchEvent(new CustomEvent('resize', { detail: { countSizeAgain: true } })); // 主动触发window resize
        await new Promise(resolve => setTimeout(resolve));
    }

    private allSlideDataConvert(): Promise<boolean> {
        return new Promise((resolve) => {
            eventBus.once(EventKeys.ALL_SLIDE_DATA_CONVERT, () => {
                resolve(true);
            });
        });
    }
}
